import React, { useContext, useEffect, useState } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  ActionIcon,
  Avatar,
  Container,
  Divider,
  Drawer,
  Menu,
  NavLink,
  Paper,
  rem,
  Switch,
  Text,
} from "@mantine/core";
import {
  useDisclosure,
  useViewportSize,
  useWindowScroll,
} from "@mantine/hooks";
import {
  IconCurrencyDollar,
  IconLogin,
  IconLogout2,
  IconMenu3,
  IconMoonStars,
  IconSun,
  IconUser,
  IconX,
} from "@tabler/icons-react";

import { motion } from "framer-motion";

import { Colors, LogoFull, LogoM } from "../../Lib/Data";
import { routes } from "./routes";

import { AppContext } from "../../Pages/Wrapper";
import "./styles.scss";

const Navbar: React.FC = () => {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  const [scroll, scrollTo] = useWindowScroll();

  const theme = context?.theme ?? "light";
  const [activeItem, setActiveItem] = useState<string>("");
  const location = useLocation();
  const { width: browserCurrentWidth } = useViewportSize();

  useEffect(() => {
    const { pathname } = location;
    const path = pathname.replaceAll("/d/", "").toLowerCase();
    routes.forEach((route) => {
      const isActive = route.link.indexOf(path.substring(0, 4)) !== -1;
      if (isActive) {
        setActiveItem(route.link);
      }
    });
  }, [location]);

  const [
    isDrawerOpened,
    { open: OpenDrawer, close: CloseDrawer, toggle: ToggleDrawer },
  ] = useDisclosure(false);

  const ToggleIcon = isDrawerOpened ? IconX : IconMenu3;

  // WATCH FOR VIEWPORT CHANGES AND CLOSE DRAWER IF SCREEN IS TOO BIG
  useEffect(() => {
    if (browserCurrentWidth > 730) {
      CloseDrawer();
    }
  }, [browserCurrentWidth]);
  return (
    <>
      <Container size="lg">
        <Drawer
          styles={{
            content: {
              backgroundColor:
                theme === "dark" ? Colors.BlackPrimary : Colors.White,
            },
          }}
          size={320}
          opened={isDrawerOpened}
          onClose={CloseDrawer}
          withCloseButton={false}
        >
          <div className="small-nav flex-col">
            <Link to="/">
              <img
                onClick={() => {
                  navigate("/");
                }}
                src={LogoFull}
                alt=""
                className="small-logo pointer"
              />
            </Link>
            {routes.map((route) => {
              return (
                <Link to={`/d/${route.link}`}>
                  <NavLink
                    styles={{
                      label: { color: Colors.BluePrimary },
                    }}
                    onClick={CloseDrawer}
                    mb={10}
                    href={`/d/${route.link}`}
                    label={route.label}
                    leftSection={
                      <route.icon
                        size="1rem"
                        color={Colors.BluePrimary}
                        stroke={1.5}
                      />
                    }
                  />
                </Link>
              );
            })}
            <Divider />

            <Text
              fz="sm"
              ml={5}
              my={10}
              fw={500}
              c={context && context.profile ? Colors.GreenPrimary : "dimmed"}
            >
              {context && context.profile
                ? context.profile.name
                : "Not Logged In"}
            </Text>
            {context?.profile?.isPromoter && (
              <Link to="/d/promoter">
                <NavLink
                  styles={{
                    label: { color: Colors.GreenSecondary },
                  }}
                  onClick={CloseDrawer}
                  label="Promoter"
                  leftSection={
                    <IconCurrencyDollar
                      size="1rem"
                      color={Colors.GreenSecondary}
                      stroke={1.5}
                    />
                  }
                />
              </Link>
            )}
            {context?.profile?.name ? (
              <>
                <NavLink
                  href="/d/profile"
                  styles={{
                    label: { color: Colors.BluePrimary },
                  }}
                  label="My Profile"
                  leftSection={
                    <IconUser
                      size="1rem"
                      color={Colors.BluePrimary}
                      stroke={1.5}
                    />
                  }
                />
                <NavLink
                  styles={{
                    label: { color: Colors.RedPrimary },
                  }}
                  onClick={context?.logout}
                  label="Logout"
                  leftSection={
                    <IconLogout2
                      size="1rem"
                      color={Colors.RedPrimary}
                      stroke={1.5}
                    />
                  }
                />
              </>
            ) : (
              <Link to="/login">
                <NavLink
                  styles={{
                    label: { color: Colors.GreenPrimary },
                  }}
                  label="Login"
                  leftSection={
                    <IconLogin
                      size="1.2rem"
                      color={Colors.GreenPrimary}
                      stroke={1.5}
                    />
                  }
                />
              </Link>
            )}
          </div>
        </Drawer>
        <div
          className={`small-togglers ${
            scroll.y > 10
              ? isDrawerOpened
                ? ""
                : `small-togglers-scrolled-${theme}`
              : ""
          }`}
        >
          <motion.span
            initial={false}
            animate={{
              left: isDrawerOpened ? "270px" : "30px",
            }}
            className="toggle-drawer"
          >
            <ActionIcon
              styles={{
                root: {
                  backgroundColor:
                    theme === "dark" ? Colors.BlackPrimary : Colors.White,
                  borderRadius: "5px",
                },
              }}
              size="lg"
              onClick={ToggleDrawer}
            >
              <ToggleIcon color={Colors.BluePrimary} size={25} />
            </ActionIcon>
          </motion.span>
          <div>
            <div className="toggle-theme-small">
              <Switch
                size="md"
                color="dark.4"
                display={isDrawerOpened ? "none" : "block"}
                checked={theme === "dark"}
                onChange={(e) => {
                  const newTheme = e.currentTarget.checked ? "dark" : "light";
                  context?.setTheme(newTheme);
                  localStorage.setItem("theme", newTheme);
                }}
                onLabel={
                  <IconSun
                    size={16}
                    stroke={2.5}
                    color="var(--mantine-color-yellow-4)"
                  />
                }
                offLabel={
                  <IconMoonStars
                    size={16}
                    stroke={2.5}
                    color="var(--mantine-color-blue-6)"
                  />
                }
              />
            </div>
          </div>
        </div>
        <div className="nav-placeholder"></div>
        <Paper shadow="sm" mt={30} className="nav-container">
          <nav
            className={`flex-row align-center justify-between nav-content nav-${theme}`}
          >
            <Link to="/">
              <img src={LogoM} alt="" className="logo" />
            </Link>
            <div className="items flex-row align-center justify-between">
              {routes.map((route) => {
                return (
                  <Link to={`/d/${route.link}`}>
                    <span
                      className={`item item-${
                        activeItem === route.link ? "active" : "inactive"
                      } flex-row align-center justify-center`}
                    >
                      <Text
                        className="label"
                        c={theme === "dark" ? Colors.White : Colors.BluePrimary}
                        tt="capitalize"
                        fz={14}
                      >
                        {route.label}
                      </Text>
                    </span>
                  </Link>
                );
              })}
            </div>
            <div className="flex-row align-center">
              <Switch
                size="md"
                color="dark.4"
                checked={theme === "dark"}
                onChange={(e) => {
                  const newTheme = e.currentTarget.checked ? "dark" : "light";
                  context?.setTheme(newTheme);
                  localStorage.setItem("theme", newTheme);
                }}
                onLabel={
                  <IconSun
                    size={16}
                    stroke={2.5}
                    color="var(--mantine-color-yellow-4)"
                  />
                }
                offLabel={
                  <IconMoonStars
                    size={16}
                    stroke={2.5}
                    color="var(--mantine-color-blue-6)"
                  />
                }
              />
            </div>
            <Menu
              shadow="md"
              width={200}
              styles={{
                dropdown: {
                  backgroundColor:
                    theme === "dark" ? Colors.BlackPrimary : Colors.White,
                },
              }}
            >
              <Menu.Target>
                <Avatar className="pointer" />
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label
                  c={
                    context && context.profile ? Colors.GreenPrimary : "dimmed"
                  }
                >
                  {context && context.profile
                    ? context.profile.name
                    : "Not Logged In"}
                </Menu.Label>
                {context && context.profile ? (
                  <>
                    <Link to="/d/profile">
                      <Menu.Item
                        color={
                          theme === "dark" ? Colors.White : Colors.BluePrimary
                        }
                        leftSection={
                          <IconUser
                            color={Colors.BluePrimary}
                            style={{ width: rem(14), height: rem(14) }}
                          />
                        }
                      >
                        <Text fz={14.5} c={Colors.BluePrimary}>
                          My Profile
                        </Text>
                      </Menu.Item>
                    </Link>
                    {context.profile.isPromoter && (
                      <Link to="/d/promoter">
                        <Menu.Item
                          color={
                            theme === "dark"
                              ? Colors.GreenPrimary
                              : Colors.GreenSecondary
                          }
                          leftSection={
                            <IconCurrencyDollar
                              color={
                                theme === "dark"
                                  ? Colors.GreenPrimary
                                  : Colors.GreenSecondary
                              }
                              style={{ width: rem(14), height: rem(14) }}
                            />
                          }
                        >
                          <Text
                            fz={14.5}
                            c={
                              theme === "dark"
                                ? Colors.GreenPrimary
                                : Colors.GreenSecondary
                            }
                          >
                            Promoter
                          </Text>
                        </Menu.Item>
                      </Link>
                    )}

                    <Menu.Divider />

                    <Menu.Item
                      color={Colors.RedPrimary}
                      onClick={context?.logout}
                      leftSection={
                        <IconLogout2
                          style={{ width: rem(14), height: rem(14) }}
                        />
                      }
                    >
                      <Text fz={14.5}>Logout</Text>
                    </Menu.Item>
                  </>
                ) : (
                  <Link to="/login">
                    <Menu.Item
                      leftSection={
                        <IconUser
                          color={Colors.BluePrimary}
                          style={{ width: rem(14), height: rem(14) }}
                        />
                      }
                    >
                      <Text fz={14.5} c={Colors.BluePrimary}>
                        Login
                      </Text>
                    </Menu.Item>
                  </Link>
                )}
              </Menu.Dropdown>
            </Menu>
          </nav>
        </Paper>
      </Container>
    </>
  );
};

export default Navbar;
